import { CegidInvoiceFormLine } from "./types";

// Fields a merged group shares: the CEGID line it is billed as.
const SHARED_FIELDS = [
  "itemCode",
  "itemDescription",
  "existsInCegid",
  "createIfMissing",
  "applyToWholeProduct",
  "extraText",
] as const;

export type CegidSharedLineFields = Pick<CegidInvoiceFormLine, (typeof SHARED_FIELDS)[number]>;

// One row of the dialog: a single order line, or the lines merged together.
export interface CegidLineUnit {
  key: string;
  lines: CegidInvoiceFormLine[];
}

export const lineKey = (line: Pick<CegidInvoiceFormLine, "orderLineId" | "isShipping">) =>
  line.isShipping ? "shipping" : (line.orderLineId ?? "");

export const sharedFields = (line: CegidInvoiceFormLine): CegidSharedLineFields =>
  Object.fromEntries(SHARED_FIELDS.map(field => [field, line[field]])) as CegidSharedLineFields;

/** Merge lines with the same CEGID SKU, price and VAT, as invoiced before. */
export const autoMergeLines = (lines: CegidInvoiceFormLine[]): CegidInvoiceFormLine[] => {
  const byKey = new Map<string, CegidInvoiceFormLine[]>();

  lines.forEach(line => {
    if (line.isShipping || !line.itemCode) {
      return;
    }

    const key = JSON.stringify([line.itemCode, line.unitPrice, line.taxCode]);

    byKey.set(key, [...(byKey.get(key) ?? []), line]);
  });

  const groupOf = new Map<string, { id: string; first: CegidInvoiceFormLine }>();

  [...byKey.entries()].forEach(([key, members]) => {
    if (members.length > 1) {
      members.forEach(member =>
        groupOf.set(lineKey(member), { id: `auto:${key}`, first: members[0] }),
      );
    }
  });

  return lines.map(line => {
    const group = groupOf.get(lineKey(line));

    return group ? { ...line, ...sharedFields(group.first), mergeGroup: group.id } : line;
  });
};

/** The dialog's rows, in order of each row's first order line. */
export const toLineUnits = (lines: CegidInvoiceFormLine[]): CegidLineUnit[] => {
  const units: CegidLineUnit[] = [];
  const groups = new Map<string, CegidLineUnit>();

  lines.forEach(line => {
    if (!line.mergeGroup) {
      units.push({ key: lineKey(line), lines: [line] });

      return;
    }

    const existing = groups.get(line.mergeGroup);

    if (existing) {
      existing.lines.push(line);

      return;
    }

    const unit = { key: `group:${line.mergeGroup}`, lines: [line] };

    groups.set(line.mergeGroup, unit);
    units.push(unit);
  });

  return units;
};

/** How many CEGID lines a merged group is billed as (one per price and VAT). */
export const invoiceLineCount = (lines: CegidInvoiceFormLine[]) =>
  new Set(lines.map(line => JSON.stringify([line.unitPrice, line.taxCode]))).size;
